<script setup lang="ts">
import { pb } from "@/pocketbase";
import Title from "@/components/Title.vue";
import { Language, Recommendation, RecommendationActivity, RecommendationTopic } from "@/model";
import { computed, ref } from "vue";
import Heading from "@/components/Heading.vue";
import Listbox from "@/components/Listbox.vue";
import Divider from "@/components/Divider.vue";
import Content from "@/components/Content.vue";

const recommendations: Recommendation[] = await pb.collection('recommendations').getFullList<Recommendation>(null, {
  sort: 'created',
});

const languages = Object.keys(Language).map(language => ({
  key: language,
  label: Language[language],
}));
const selectedLanguages = ref(languages);

const topics = Object.keys(RecommendationTopic).map(topic => ({
  key: topic,
  label: RecommendationTopic[topic],
}));
const selectedTopics = ref(topics);

const activities = computed(() =>
    [...new Set(recommendations.map(recommendation => recommendation.activity))].sort((a, b) => a.localeCompare(b))
);

function getImageUrl(recommendation: Recommendation, file: string): string {
  return pb.files.getUrl(recommendation, file);
}

function filterRecommendations(activity: RecommendationActivity): Recommendation[] {
  return recommendations.filter(recommendation => {
    if (recommendation.activity !== activity) {
      return false;
    }

    if (!selectedLanguages.value.map(lang => lang.key).includes(recommendation.language)) {
      return false;
    }

    for (const topic of selectedTopics.value) {
      if (recommendation.topics.includes(topic.key)) {
        return true;
      }
    }

    return false;
  });
}
</script>

<template>
  <Title>Recommendations</Title>
  <Divider/>
  <Content>
    <p>
      Recommendations for reading and watching from blogs to books to talks and conferences.
    </p>

    <div class="grid md:grid-cols-3 gap-2 mb-4 lg:mb-12">
      <Listbox label="Languages" optional multiple v-model="selectedLanguages" :options="languages"/>
      <div class="md:col-span-2">
        <Listbox label="Topics" optional multiple v-model="selectedTopics" :options="topics"/>
      </div>
    </div>

    <template v-for="activity in activities">
      <Heading>{{ RecommendationActivity[activity] }}</Heading>

      <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        <a v-for="recommendation in filterRecommendations(activity)"
           :href="recommendation.url" target="_blank"
           class="flex justify-end rounded-lg bg-gray-100 dark:bg-gray-900 bg-no-repeat bg-center bg-cover"
           :style="recommendation.image.length ? `background-image: url('${getImageUrl(recommendation, recommendation.image)}')` : ''">
          <div class="basis-1/4"></div>
          <div class="basis-3/4 p-2 bg-black bg-opacity-60 dark:bg-opacity-80 text-white">
            <h3 class="font-bold text-xs">{{ recommendation.title }}</h3>
            <div class="mt-1 text-xs">{{ recommendation.description }}</div>
          </div>
        </a>
      </div>
    </template>
  </Content>
</template>

<style scoped lang="scss">
</style>
